import { marked } from 'marked';
import DOMPurify from 'dompurify';

export function renderMarkdown(text) {
  if (!text) return "";
  try {
    const html = marked.parse(text, { breaks: true, gfm: true });
    return DOMPurify.sanitize(html);
  } catch (_) {
    return DOMPurify.sanitize(text.replace(/</g, "&lt;").replace(/\n/g, "<br>"));
  }
}

export function escapeHtml(s) {
  return (s || "").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c]));
}

/* -------- Save / Download helpers -------- */
export function nowStamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}
export function nowReadable() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export function slugify(s) {
  return (s || "untitled").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}

/* =================================================================
   Save-As — universal file save with the File System Access API.
   Tries window.showSaveFilePicker() (Chromium browsers — Chrome, Edge,
   Brave, Opera) so the user can pick a custom filename AND location.
   Falls back to the classic <a download> when the API is unavailable
   (Firefox, Safari) or the picker call throws an unexpected error.
   Returns an object so callers can distinguish:
     { ok: true, mode: "save-picker"|"anchor" }   → toast "saved"
     { ok: false, cancelled: true }              → user cancelled, no toast
   ================================================================= */
export function filenameToFilePickerTypes(filename) {
  const m = String(filename || "").match(/\.([^.]+)$/);
  const ext = (m ? m[1] : "txt").toLowerCase();
  const map = {
    md:       { description: "Markdown",                accept: { "text/markdown": [".md", ".markdown"] } },
    markdown: { description: "Markdown",                accept: { "text/markdown": [".md", ".markdown"] } },
    txt:      { description: "Plain text",              accept: { "text/plain": [".txt"] } },
    json:     { description: "JSON",                    accept: { "application/json": [".json"] } },
    docx:     { description: "Word document",           accept: { "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"] } },
    pdf:      { description: "PDF",                     accept: { "application/pdf": [".pdf"] } },
    xlsx:     { description: "Excel spreadsheet",       accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] } },
    pptx:     { description: "PowerPoint presentation", accept: { "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"] } },
    html:     { description: "HTML",                    accept: { "text/html": [".html", ".htm"] } },
    csv:      { description: "CSV",                     accept: { "text/csv": [".csv"] } },
  };
  return [map[ext] || { description: "All files", accept: { "application/octet-stream": ["." + ext] } }];
}

export async function saveBlobAs(blob, suggestedName) {
  /* Path 1: File System Access API — true Save-As dialog */
  if (typeof window.showSaveFilePicker === "function") {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName,
        types: filenameToFilePickerTypes(suggestedName),
        excludeAcceptAllOption: false,
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { ok: true, mode: "save-picker" };
    } catch (e) {
      /* User cancelled the picker — return cleanly so caller skips toast */
      if (e && (e.name === "AbortError" || e.code === 20)) {
        return { ok: false, cancelled: true };
      }
      /* SecurityError, NotAllowedError, etc. — fall through to anchor download */
      console.warn("[saveBlobAs] showSaveFilePicker failed, falling back to anchor:", e);
    }
  }
  /* Path 2: classic <a download> — Firefox, Safari, or fallback after error */
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
  return { ok: true, mode: "anchor" };
}

export async function downloadText(filename, content, mime) {
  const blob = new Blob([content], { type: mime || "text/markdown;charset=utf-8" });
  return saveBlobAs(blob, filename);
}

export function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
  }
  // Fallback
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); document.body.removeChild(ta); return Promise.resolve(true); }
  catch (_) { document.body.removeChild(ta); return Promise.resolve(false); }
}

export function mdToHtml(md) {
  if (!md) return "";
  try {
    const html = marked.parse(md, { breaks: true, gfm: true });
    return DOMPurify.sanitize(html);
  } catch (_) { return ""; }
}

export function stripMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/```[\s\S]*?```/g, m => m.replace(/```\w*\n?/g, "").replace(/```/g, ""))
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^[-*]\s+/gm, "• ")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^>\s+/gm, "")
    .trim();
}

export function truncate(s, n) {
  if (!s) return "";
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

/* triggerDownload now delegates to saveBlobAs. Returning a Promise lets
   binary exporters (Word/Excel/PPT/PDF) await + propagate the cancelled
   flag to mountExportMenu, which uses it to skip the "saved" toast. */
export async function triggerDownload(blob, filename) {
  return saveBlobAs(blob, filename);
}

/* USD formatter — shared between cost legend and tooltips */
export function fmtUsd(amount) {
  if (!amount || amount < 0.00001) return "$0.00";
  if (amount < 0.001)   return "$" + amount.toFixed(5);
  if (amount < 0.01)    return "$" + amount.toFixed(4);
  if (amount < 1)       return "$" + amount.toFixed(3);
  return "$" + amount.toFixed(2);
}
